import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { getApiBase } from "../apiBase";
import LiveConversations from "./LiveConversations";
import {
  FaGlobe,
  FaTheaterMasks,
  FaAlignLeft,
  FaBookOpen,
  FaEdit,
  FaCheck,
  FaCopy,
  FaSave,
  FaThumbsUp,
  FaStar,
  FaCheckCircle,
  FaClock,
  FaShareAlt,
  FaUser,
  FaTimes,
} from "react-icons/fa";
import { FaBook, FaFire, FaMessage } from "react-icons/fa6";
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
  ResponsiveContainer,
} from "recharts";

/* ==========================================
   DESIGN TOKENS (same theme, flat + professional)
   Plum:  #3D2542 (ink)  #5B3A60 (brand)  #F0E6F5 (tint)  #FAF8FB (page bg)
   Gold:  #B89D4F (accent)  #A88D3F (accent-dark)
   Line:  #E4DCE6
   Text:  #7A6B7E (muted)
   State: success #1F9D6C / #E8F7F0   error #C62828 / #FDEEEE   pending #B8860B / #FBF3DC
   ========================================== */

const TimelineItem = ({ icon, label, name, value, editMode, form, setForm }) => {
  const isLong = ["description", "background", "tone_and_persona"].includes(name);

  return (
    <div className="relative pl-8 mb-6">
      <div className="absolute left-0 top-1.5 w-2.5 h-2.5 bg-[#B89D4F] border-2 border-white rounded-full" />
      <div className="flex items-center gap-2.5 mb-2">
        <div className="w-8 h-8 rounded-md bg-[#F0E6F5] flex items-center justify-center text-[#5B3A60] text-sm">
          {icon}
        </div>
        <p className="text-xs uppercase tracking-wide text-[#5B3A60] font-semibold">{label}</p>
      </div>
      {editMode ? (
        isLong ? (
          <textarea
            value={form[name] ?? ""}
            onChange={(e) => setForm((prev) => ({ ...prev, [name]: e.target.value }))}
            rows={4}
            className="w-full px-3.5 py-2.5 rounded-md border border-[#E4DCE6] bg-white text-[#3D2542] outline-none focus:ring-2 focus:ring-[#5B3A60]/10 focus:border-[#5B3A60] transition-colors text-sm leading-relaxed placeholder:text-[#C9BDCC]"
            placeholder={`Enter ${label.toLowerCase()}...`}
          />
        ) : (
          <input
            value={form[name] ?? ""}
            onChange={(e) => setForm((prev) => ({ ...prev, [name]: e.target.value }))}
            className="w-full px-3.5 py-2.5 rounded-md border border-[#E4DCE6] bg-white text-[#3D2542] outline-none focus:ring-2 focus:ring-[#5B3A60]/10 focus:border-[#5B3A60] transition-colors text-sm"
            placeholder={`Enter ${label.toLowerCase()}...`}
          />
        )
      ) : (
        <div className="w-full px-3.5 py-2.5 rounded-md border border-[#E4DCE6] bg-[#FAF8FB] text-[#3D2542] text-sm leading-relaxed">
          {value || <span className="text-[#A89CA5] italic">Not specified</span>}
        </div>
      )}
    </div>
  );
};

const StatCard = ({ icon, label, value, tooltip }) => (
  <div className="relative group">
    {/* Card */}
    <div className="h-[92px] bg-white rounded-lg px-4 py-3 border border-[#E4DCE6] hover:border-[#B89D4F]/50 transition-colors duration-150 flex flex-col justify-between">
      <div className="flex items-center gap-2">
        <div className="w-8 h-8 rounded-md bg-[#F0E6F5] flex items-center justify-center text-[#5B3A60] text-sm shrink-0">
          {icon}
        </div>
        <p className="text-[11px] font-semibold text-[#7A6B7E] uppercase tracking-wide leading-tight break-words">
          {label}
        </p>
      </div>
      <p className="text-xl font-bold text-[#3D2542] text-right">{value}</p>
    </div>

    {/* Tooltip — now outside the card, drops below */}
    {tooltip && (
      <div className="absolute inset-x-0 top-full mt-1.5 bg-[#3D2542] px-4 py-2.5 text-[10px] text-white opacity-0 -translate-y-1 group-hover:opacity-100 group-hover:translate-y-0 transition-all duration-150 pointer-events-none z-20 rounded-md shadow-lg">
        <span className="font-semibold">{label}</span>
        <span className="block mt-0.5 opacity-90 leading-snug">{tooltip}</span>
      </div>
    )}
  </div>
);

const StatCardSkeleton = () => <div className="h-[92px] bg-white rounded-lg animate-pulse border border-[#E4DCE6]" />;

const ModalButton = ({ children, onClick, variant = "primary", disabled, className = "", icon: Icon }) => {
  const variants = {
    primary: "bg-[#5B3A60] text-white hover:bg-[#4A2E50] border border-[#5B3A60]",
    secondary: "bg-white text-[#5B3A60] border border-[#E4DCE6] hover:border-[#5B3A60] hover:bg-[#FAF8FB]",
  };
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      className={`px-5 py-2.5 rounded-md font-semibold text-sm transition-colors flex items-center gap-2 disabled:opacity-60 disabled:cursor-not-allowed ${variants[variant]} ${className}`}
    >
      {Icon && <Icon size={13} />}
      {children}
    </button>
  );
};

export default function BotOverview() {
  const { tenantId, botId } = useParams();
  const [activeTab, setActiveTab] = useState("overview");

  const [botData, setBotData] = useState(null);
  const [botStats, setBotStats] = useState(null);
  const [toast, setToast] = useState(null);
  const [shareUrl, setShareUrl] = useState(null);
  const [showShareModal, setShowShareModal] = useState(false);
  const [shareCopied, setShareCopied] = useState(false);
  const [editingName, setEditingName] = useState(false);
  const [botNameInput, setBotNameInput] = useState("");
  const [savingName, setSavingName] = useState(false);
  const [chartData, setChartData] = useState([]);
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");

  const [period, setPeriod] = useState("day");

  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [detailsEditMode, setDetailsEditMode] = useState(false);
  const [savingDetails, setSavingDetails] = useState(false);

  const [detailsForm, setDetailsForm] = useState({
    domain: "",
    description: "",
    tone_and_persona: "",
    background: "",
  });

  const [isLoading, setIsLoading] = useState(true);

  const CustomTooltip = ({ active, payload, label }) => {
    if (!active || !payload || !payload.length) return null;

    return (
      <div className="bg-white shadow-md border border-[#E4DCE6] rounded-md px-4 py-3">
        <p className="text-xs text-[#7A6B7E] mb-1">{formatXAxis(label)}</p>
        {payload.map((item, index) => (
          <div key={index} className="flex justify-between gap-6 text-sm font-semibold">
            <span className="text-[#5B3A60] capitalize">{item.name}</span>
            <span className="text-[#3D2542]">{item.value}</span>
          </div>
        ))}
      </div>
    );
  };

  const copyToClipboard = async (text) => {
    try {
      if (navigator.clipboard && window.isSecureContext) {
        await navigator.clipboard.writeText(text);
        return true;
      }
      const textarea = document.createElement("textarea");
      textarea.value = text;
      textarea.style.position = "fixed";
      textarea.style.opacity = "0";
      document.body.appendChild(textarea);
      textarea.focus();
      textarea.select();
      const ok = document.execCommand("copy");
      document.body.removeChild(textarea);
      return ok;
    } catch (err) {
      console.error("Copy failed:", err);
      return false;
    }
  };

  const handleSaveBotName = async () => {
    if (!botNameInput.trim()) return;
    try {
      setSavingName(true);
      const res = await fetch(`${getApiBase()}/tenant/${tenantId}/bot/${botId}/update`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ bot_name: botNameInput }),
      });
      const data = await res.json();
      if (data.status === "success") {
        setBotData((prev) => ({ ...prev, bot_name: botNameInput }));
        setEditingName(false);
        showToast("Bot name updated");
      } else {
        showToast("Update failed", "error");
      }
    } catch {
      showToast("Something went wrong", "error");
    } finally {
      setSavingName(false);
    }
  };

  const formatXAxis = (value) => {
    if (!value) return "";

    if (value.length === 7) {
      const date = new Date(value + "-01");
      return date.toLocaleDateString("en-US", { month: "short", year: "numeric" });
    }

    const date = new Date(value);
    return date.toLocaleDateString("en-US", { day: "2-digit", month: "short" });
  };

  const handleOpenShare = () => {
    const url = `${window.location.origin}/chat/${tenantId}/${botId}`;
    setShareUrl(url);
    setShareCopied(false);
    setShowShareModal(true);
  };

  const handleCopyShare = async () => {
    const ok = await copyToClipboard(shareUrl);
    if (ok) setShareCopied(true);
    else showToast("Copy failed", "error");
    setTimeout(() => setShareCopied(false), 1500);
  };

  const showToast = (msg, type = "success") => {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 2500);
  };

  useEffect(() => {
    const load = async () => {
      setIsLoading(true);
      try {
        const statsRes = await fetch(`${getApiBase()}/bot/${botId}/overview`);
        const statsJson = await statsRes.json();
        setBotStats(statsJson);

        const botRes = await fetch(`${getApiBase()}/tenant/${tenantId}/bot/${botId}/details`);
        const botJson = await botRes.json();
        setBotData(botJson.data);

        const query = new URLSearchParams({
          period,
          ...(startDate && { start_date: startDate }),
          ...(endDate && { end_date: endDate }),
        });

        const trendRes = await fetch(`${getApiBase()}/bot/${botId}/trend?${query.toString()}`);
        const trendJson = await trendRes.json();
        setChartData(trendJson);
      } catch (err) {
        console.error("Overview load failed:", err);
      } finally {
        setIsLoading(false);
      }
    };
    load();
  }, [tenantId, botId, period, startDate, endDate]);

  useEffect(() => {
    if (!startDate || !endDate) {
      const today = new Date();
      const past = new Date();
      past.setDate(today.getDate() - 30);

      setStartDate(past.toISOString().split("T")[0]);
      setEndDate(today.toISOString().split("T")[0]);
    }
  }, []);

  const handleOpenDetails = () => {
    setDetailsForm({
      domain: botData?.bot_industry || "",
      description: botData?.bot_description || "",
      tone_and_persona: botData?.bot_persona || "",
      background: botData?.background || "",
    });
    setDetailsEditMode(false);
    setShowDetailsModal(true);
  };

  const handleSaveDetails = async () => {
    try {
      setSavingDetails(true);
      const res = await fetch(`${getApiBase()}/tenant/${tenantId}/bot/${botId}/update`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(detailsForm),
      });
      const data = await res.json();
      if (data.status === "success") {
        showToast("Bot updated successfully");
        setBotData((prev) => ({
          ...prev,
          bot_industry: detailsForm.domain,
          bot_description: detailsForm.description,
          bot_persona: detailsForm.tone_and_persona,
        }));
        setDetailsEditMode(false);
        setShowDetailsModal(false);
      } else {
        showToast("Update failed", "error");
      }
    } catch {
      showToast("Something went wrong", "error");
    } finally {
      setSavingDetails(false);
    }
  };

  const safe = {
    total_conversations: botStats?.total_conversations ?? 0,
    total_messages: botStats?.total_messages ?? 0,
    total_documents: botStats?.total_documents ?? 0,
    thumbs_up: botStats?.message_feedback_thumbs_up ?? 0,
    avg_stars: botStats?.conversation_feedback_avg_stars ?? 0,
    avg_latency: botStats?.avg_latency_ms ?? 0,
    escalation_rate: botStats?.escalation_rate_percent ?? 0,
    success_rate: botStats?.success_rate ?? null,
    valid_sessions_for_success_rate: botStats?.valid_sessions_for_success_rate ?? 0,
    successful_sessions_no_fallback: botStats?.successful_sessions_no_fallback ?? 0,
    created: botStats?.bot_created_at ?? "",
    total_users: botStats?.total_users ?? 0,
    live_active_users: botStats?.live_active_users ?? 0,
  };

  return (
    <div className="min-h-screen bg-[#FAF8FB] px-4 md:px-8 lg:px-12 py-8 md:py-10 font-sans">
      <AnimatePresence>
        {toast && (
          <motion.div
            initial={{ opacity: 0, y: -12 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -12 }}
            transition={{ duration: 0.18 }}
            className={`fixed top-5 right-5 z-50 w-80 px-4 py-3.5 rounded-lg flex items-center gap-3 border shadow-sm bg-white ${
              toast.type === "success" ? "border-l-4 border-l-[#1F9D6C]" : "border-l-4 border-l-[#C62828]"
            } border-t border-r border-b border-[#E4DCE6]`}
          >
            {toast.type === "success" ? (
              <FaCheckCircle className="text-[#1F9D6C]" size={16} />
            ) : (
              <FaTimes className="text-[#C62828]" size={16} />
            )}
            <span className="font-medium text-sm text-[#3D2542]">{toast.msg}</span>
          </motion.div>
        )}
      </AnimatePresence>

      {/* HEADER */}
      <div className="mb-8 flex flex-col md:flex-row md:items-center justify-between gap-5 pb-6 border-b border-[#E4DCE6]">
        <div className="flex flex-col gap-2.5">
          <div className="flex items-center gap-4 flex-wrap">
            <span className="w-1.5 h-7 bg-[#B89D4F] rounded-full" />
            {editingName ? (
              <div className="flex items-center gap-2 bg-white px-3 py-2 rounded-md border border-[#B89D4F] shadow-sm">
                <input
                  value={botNameInput}
                  onChange={(e) => setBotNameInput(e.target.value)}
                  className="bg-transparent outline-none text-xl font-bold text-[#3D2542] min-w-[160px] max-w-[320px]"
                  style={{ width: `${Math.max(botNameInput.length, 12)}ch` }}
                  autoFocus
                />
                <button
                  onClick={handleSaveBotName}
                  disabled={savingName}
                  className="w-8 h-8 rounded-md flex items-center justify-center bg-[#E8F7F0] text-[#1F9D6C] hover:bg-[#D5F0E3] transition-colors"
                >
                  <FaCheck size={13} />
                </button>
                <button
                  onClick={() => {
                    setEditingName(false);
                    setBotNameInput(botData?.bot_name || "");
                  }}
                  className="w-8 h-8 rounded-md flex items-center justify-center bg-[#FDEEEE] text-[#C62828] hover:bg-[#FADEDE] transition-colors"
                >
                  <FaTimes size={13} />
                </button>
              </div>
            ) : (
              <div className="flex items-center gap-3 group">
                <span className="text-2xl font-bold text-[#3D2542] tracking-tight">{botData?.bot_name}</span>
                <button
                  onClick={() => {
                    setBotNameInput(botData?.bot_name || "");
                    setEditingName(true);
                  }}
                  className="w-8 h-8 rounded-md flex items-center justify-center bg-[#F0E6F5] text-[#5B3A60] hover:bg-[#E8D9F0] transition-colors"
                  aria-label="Edit bot name"
                >
                  <FaEdit size={13} />
                </button>
              </div>
            )}
            <span className="flex items-center gap-1.5 text-xs font-semibold text-[#1F9D6C] bg-[#E8F7F0] px-2.5 py-1 rounded border border-[#BFE8D5]">
              <span className="w-1.5 h-1.5 bg-[#1F9D6C] rounded-full" />
              Online
            </span>
          </div>
          <div className="flex items-center gap-3 text-sm text-[#7A6B7E] ml-5">
            <span>
              Created on{" "}
              <span className="font-semibold text-[#3D2542]">
                {botData?.created_at
                  ? new Date(botData.created_at).toLocaleDateString("en-US", { year: "numeric", month: "short", day: "numeric" })
                  : "—"}
              </span>
            </span>
            <span className="w-1 h-1 bg-[#C9BDCC] rounded-full" />
            <span>
              Channel: <span className="font-semibold text-[#3D2542]">WebChat</span>
            </span>
          </div>
        </div>

        <div className="flex items-center gap-3">
          <button
            onClick={handleOpenShare}
            className="px-4 py-2.5 rounded-md bg-white border border-[#E4DCE6] text-[#5B3A60] hover:border-[#5B3A60] transition-colors flex items-center gap-2 font-semibold text-xs"
          >
            <FaShareAlt size={12} /> Share
          </button>

          <button
            onClick={handleOpenDetails}
            className="px-4 py-2.5 rounded-md bg-[#5B3A60] text-white hover:bg-[#4A2E50] transition-colors flex items-center gap-2 font-semibold text-xs"
          >
            <FaBookOpen size={12} /> Agent Details
          </button>
        </div>
      </div>

      {/* STAT GRID */}
      {isLoading ? (
        <div className="mb-10 grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4">
          {[...Array(9)].map((_, i) => (
            <StatCardSkeleton key={i} />
          ))}
        </div>
      ) : (
        botStats && (
          <div className="mb-10 grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4">
            <StatCard icon={<FaMessage />} label="Total Conversations" value={safe.total_conversations} tooltip="Total number of chat sessions started by users." />
            <StatCard icon={<FaAlignLeft />} label="Messages Processed" value={safe.total_messages} tooltip="Total number of messages exchanged between users and the bot." />
            <StatCard icon={<FaBook />} label="Knowledge Documents" value={safe.total_documents} tooltip="Total documents uploaded to enhance the bot's knowledge base." />
            <StatCard icon={<FaThumbsUp />} label="Message Feedback" value={safe.thumbs_up} tooltip="Number of positive feedback (thumbs up) received on bot responses." />
            <StatCard icon={<FaStar />} label="Conversation Feedback" value={safe.avg_stars} tooltip="Average rating given by users after completing conversations." />
            <StatCard icon={<FaFire />} label="Escalation Rate" value={`${safe.escalation_rate}%`} tooltip="Percentage of conversations transferred to a human agent." />
            <StatCard
              icon={<FaCheckCircle />}
              label="Success Rate"
              value={safe.success_rate != null ? `${(safe.success_rate * 100).toFixed(1)}%` : "—"}
              tooltip={`Measures how often the bot completes conversations successfully without human fallback. Valid sessions: ${safe.valid_sessions_for_success_rate}`}
            />
            <StatCard icon={<FaClock />} label="Avg Response Time(s)" value={`${safe.avg_latency.toFixed(2)}`} tooltip="Average time taken by the bot to respond to user messages." />
            <StatCard icon={<FaUser />} label="Users" value={safe.total_users} tooltip="Total unique users who have interacted with this bot." />
            <StatCard icon={<FaUser />} label="Active Users" value={safe.live_active_users} tooltip="Users active in the last few minutes." />
          </div>
        )
      )}

      {/* ACTIVITY CHART */}
      {chartData.length > 0 && (
        <div className="mb-10">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-4">
            <h2 className="text-xs font-bold text-[#5B3A60] uppercase tracking-wide flex items-center gap-2">
              <span className="w-1.5 h-4 bg-[#B89D4F] rounded-full" />
              Activity
            </h2>
            <div className="flex items-center gap-2 bg-white rounded-md p-1.5 border border-[#E4DCE6]">
              <select
                value={period}
                onChange={(e) => setPeriod(e.target.value)}
                className="px-2.5 py-1.5 rounded border border-[#E4DCE6] text-xs font-semibold text-[#3D2542] bg-white outline-none focus:border-[#5B3A60]"
              >
                <option value="day">Daily</option>
                <option value="month">Monthly</option>
              </select>

              <input
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                className="px-2.5 py-1.5 rounded border border-[#E4DCE6] text-xs text-[#3D2542] outline-none focus:border-[#5B3A60]"
              />

              <input
                type="date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                className="px-2.5 py-1.5 rounded border border-[#E4DCE6] text-xs text-[#3D2542] outline-none focus:border-[#5B3A60]"
              />
            </div>
          </div>

          <div className="bg-white rounded-lg p-6 border border-[#E4DCE6]">
            <ResponsiveContainer width="100%" height={300}>
              <AreaChart data={chartData} margin={{ top: 10, right: 20, bottom: 0, left: 0 }}>
                <defs>
                  <linearGradient id="convGradient" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#5B3A60" stopOpacity={0.35} />
                    <stop offset="100%" stopColor="#5B3A60" stopOpacity={0.02} />
                  </linearGradient>

                  <linearGradient id="msgGradient" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#B89D4F" stopOpacity={0.35} />
                    <stop offset="100%" stopColor="#B89D4F" stopOpacity={0.02} />
                  </linearGradient>
                </defs>

                <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#E4DCE6" strokeOpacity={0.6} />

                <XAxis dataKey="day" axisLine={false} tickLine={false} tickFormatter={formatXAxis} tick={{ fontSize: 11, fill: "#7A6B7E" }} />
                <YAxis axisLine={false} tickLine={false} tick={{ fontSize: 11, fill: "#7A6B7E" }} />

                <Tooltip content={<CustomTooltip />} />

                <Area type="monotone" dataKey="conversations" stroke="#5B3A60" strokeWidth={1.5} activeDot={{ r: 5 }} fill="url(#convGradient)" dot={false} />
                <Area type="monotone" dataKey="messages" stroke="#B89D4F" strokeWidth={1.5} activeDot={{ r: 5 }} fill="url(#msgGradient)" dot={false} />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </div>
      )}

      {/* AGENT DETAILS MODAL */}
      <AnimatePresence>
        {showDetailsModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.15 }}
            className="fixed inset-0 z-50 flex items-center justify-center bg-[#3D2542]/50 p-4"
          >
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: 10 }}
              transition={{ duration: 0.15 }}
              className="bg-white w-full max-w-2xl rounded-lg overflow-hidden flex flex-col max-h-[92vh] border border-[#E4DCE6]"
            >
              <div className="px-6 py-4 border-b border-[#E4DCE6] flex items-center justify-between bg-[#FAF8FB]">
                <h3 className="text-[15px] font-bold text-[#3D2542] flex items-center gap-2.5">
                  <div className="w-8 h-8 rounded-md bg-[#F0E6F5] flex items-center justify-center text-[#5B3A60]">
                    <FaBookOpen size={13} />
                  </div>
                  Agent Configuration
                </h3>
                <button
                  onClick={() => setShowDetailsModal(false)}
                  className="w-8 h-8 rounded-md flex items-center justify-center text-[#7A6B7E] hover:text-[#3D2542] hover:bg-[#F0E6F5] transition-colors"
                >
                  <FaTimes size={16} />
                </button>
              </div>

              <div className="p-6 overflow-y-auto">
                <div className="relative ml-3">
                  <div className="absolute left-1 top-0 w-px h-full bg-[#E4DCE6]" />

                  <TimelineItem icon={<FaGlobe />} label="Industry" name="domain" value={detailsForm.domain} editMode={detailsEditMode} form={detailsForm} setForm={setDetailsForm} />
                  <TimelineItem
                    icon={<FaTheaterMasks />}
                    label="Voice and Personality"
                    name="tone_and_persona"
                    value={detailsForm.tone_and_persona}
                    editMode={detailsEditMode}
                    form={detailsForm}
                    setForm={setDetailsForm}
                  />
                  <TimelineItem
                    icon={<FaAlignLeft />}
                    label="Agent Identity and Mission"
                    name="description"
                    value={detailsForm.description}
                    editMode={detailsEditMode}
                    form={detailsForm}
                    setForm={setDetailsForm}
                  />
                  <TimelineItem
                    icon={<FaBookOpen />}
                    label="Extra Intelligence"
                    name="background"
                    value={detailsForm.background}
                    editMode={detailsEditMode}
                    form={detailsForm}
                    setForm={setDetailsForm}
                  />
                </div>
              </div>

              <div className="px-6 py-4 border-t border-[#E4DCE6] bg-[#FAF8FB] flex justify-end gap-3">
                {detailsEditMode ? (
                  <>
                    <ModalButton
                      variant="secondary"
                      onClick={() => {
                        setDetailsEditMode(false);
                        setDetailsForm({
                          domain: botData?.bot_industry || "",
                          description: botData?.bot_description || "",
                          tone_and_persona: botData?.bot_persona || "",
                          background: botData?.background || "",
                        });
                      }}
                    >
                      Cancel
                    </ModalButton>
                    <ModalButton variant="primary" onClick={handleSaveDetails} disabled={savingDetails} icon={FaSave}>
                      {savingDetails ? "Saving…" : "Save Changes"}
                    </ModalButton>
                  </>
                ) : (
                  <ModalButton variant="primary" onClick={() => setDetailsEditMode(true)} icon={FaEdit}>
                    Edit Details
                  </ModalButton>
                )}
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* SHARE MODAL */}
      <AnimatePresence>
        {showShareModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.15 }}
            className="fixed inset-0 z-50 flex items-center justify-center bg-[#3D2542]/50 p-4"
          >
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: 10 }}
              transition={{ duration: 0.15 }}
              className="bg-white w-full max-w-md rounded-lg p-6 border border-[#E4DCE6]"
            >
              <h3 className="text-[15px] font-bold text-[#3D2542] mb-4 flex items-center gap-2.5">
                <div className="w-8 h-8 rounded-md bg-[#FBF3DC] flex items-center justify-center text-[#A88D3F]">
                  <FaShareAlt size={13} />
                </div>
                Shareable URL
              </h3>
              <div className="flex items-center gap-3 border border-[#E4DCE6] rounded-md px-4 py-3 text-sm text-[#3D2542] bg-[#FAF8FB]">
                <span className="truncate flex-1 font-mono text-xs">{shareUrl}</span>
                <button
                  onClick={handleCopyShare}
                  className={`flex items-center gap-1.5 px-3 py-1.5 rounded font-semibold text-xs transition-colors ${
                    shareCopied ? "bg-[#E8F7F0] text-[#1F9D6C]" : "bg-white border border-[#E4DCE6] text-[#5B3A60] hover:border-[#5B3A60]"
                  }`}
                >
                  {shareCopied ? <FaCheck size={12} /> : <FaCopy size={12} />}
                </button>
              </div>
              <div className="mt-6 flex justify-end">
                <ModalButton variant="secondary" onClick={() => setShowShareModal(false)}>
                  Close
                </ModalButton>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}